import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import Image from 'components/Common/Image'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/getShareModalConfig'
import AlwaysOnSummary from './AlwaysOnSummary'

import { useAppDispatch } from 'hooks/reduxHooks'

import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import OfferBNPLAd from '../OfferBNPLAd'
import GeoContext from 'contexts/geoContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import TextLink from 'components/Luxkit/TextLink'
import OfferDescription from '../OfferDescription'
import Breakpoint from 'components/Common/Breakpoint'
import InlineGroup from 'components/Common/InlineGroup'
import LineEyeIcon from 'components/Luxkit/Icons/line/LineEyeIcon'
import Caption from 'components/Luxkit/Typography/Caption'
import { getOfferExtra, OFFER_VIEWS_24_HOURS_THRESHOLD } from 'selectors/offerExtraSelector'
import { fetchOfferExtraById } from 'actions/OfferActions'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/depositsUtils'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import cn from 'clsx'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  OfferPackageOptionView,
  getTacticalPackageOptions,
} from 'selectors/offerPageSelectors'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import config from 'constants/config'
import { isIntroMapEnabled, shouldShowSalesUSP } from 'selectors/featuresSelectors'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import ContactUSP from '../ContactUSP'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PropertyMap from 'components/Common/PropertyMap/PropertyMap'
import { OFFER_SUMMARY_MAP_HEIGHT, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'
import invariant from 'tiny-invariant'
import OfferPromotions from 'components/OfferPage/OfferPromotions'

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
  }
`

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content 1fr;
  }
`

const OfferIntroSection = styled(BodyText)`
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const Summary = styled(AlwaysOnSummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const OfferOtherActions = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  grid-row: 3;
  grid-column: 1;

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
    grid-row: 1;
    grid-column: 2;
  }
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const WatchVideoButton = styled(TextButton)`
  margin-top: ${rem(12)};
`

const MobileBNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.five};
  order: 1;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const OfferViews = styled.div`
  display: flex;
  padding: ${rem(8)};
  justify-content: center;
  color: ${props => props.theme.palette.messaging.fast.main};
  background-color: ${props => props.theme.palette.messaging.fast.light};
`

const MobileOfferViews = styled(OfferViews)`
  position: relative;
  top: -${rem(20)};
  margin: 0 -${rem(20)};
`

const CenteredLabelGroup = styled(LabelGroup)`
  align-items: center;
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  promotions: Array<OfferPackageOptionView>;
  customerPortalURL: string;
  checkIn: moment.Moment;
  offer: App.Offer;
  pathname: string;
  memberId: string;
  onWatchVideo: () => void;
  className?: string;
  onViewPackageOptions: () => void;
  offerExtra: App.OfferExtra;
  shouldShowSalesPhoneUSP: boolean;
  defaultDepositAmountPercentage: number;
  isMapEnabled: boolean;
}

function AlwaysOnIntro(props: Props) {
  const {
    offer,
    promotions,
    onWatchVideo,
    memberId,
    pathname,
    customerPortalURL,
    className,
    onViewPackageOptions,
    offerExtra,
    cheapestOption,
    cheapestSelectedOption,
    checkIn,
    shouldShowSalesPhoneUSP,
    defaultDepositAmountPercentage,
    isMapEnabled,
  } = props

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentCurrency, currentRegionCode, showTaxExclusivePrices } = useContext(GeoContext)
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: offer.images[0],
    memberId,
    offerId: offer.id,
    offerLocation: location,
    pathname,
    region: currentRegionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const packageView = cheapestSelectedOption ?? cheapestOption
  const { hotelPrice, propertyFees, package: pkg } = packageView
  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees
  const property = offer.property
  invariant(property, 'Property missing from offer')

  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkIn, totalPrice, pkg)

  const urgencyToday = useMemo(() => {
    if (offerExtra) {
      return {
        isActive: offerExtra.viewsInLast24Hours >= OFFER_VIEWS_24_HOURS_THRESHOLD,
        captionText: `${offerExtra.viewsInLast24Hours} people viewed this offer today`,
        Icon: LineEyeIcon,
      }
    }
    return { isActive: false } as const
  }, [offerExtra])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  return (
    <OfferIntroWrapper>
      { urgencyToday.isActive && <Breakpoint max="mobile">
        <MobileOfferViews>
          <InlineGroup vertical="center">
            <urgencyToday.Icon />&nbsp;
            <Caption variant="medium" weight="bold">{urgencyToday.captionText}</Caption>
          </InlineGroup>
        </MobileOfferViews>
      </Breakpoint> }
      <OfferIntroGrid data-testid="offer-intro">
        <CenteredLabelGroup>
          {currentRegionCode !== 'IN' && (
            <PromotionLabel variant="default" icon={<LineUserIcon />}>
              24-7 support
            </PromotionLabel>
          )}
          {depositsAvailable && <DepositsAvailableTag offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>}
        </CenteredLabelGroup>
        <OfferOtherActions>
          <TripGuard inverse>
            <ShareModal shareConfig={shareConfig}>
              <ShareWithFriendsButton outdent="right" />
            </ShareModal>
          </TripGuard>
          <TripGuard>
            <ShareModal shareConfig={shareConfig}>
              <ShareWithFriendsButton />
            </ShareModal>
            <BookmarkButton offer={offer} />
          </TripGuard>
        </OfferOtherActions>
        <Header>
          <VerticalSpacer gap={8}>
            <BodyTextBlock
              variant="medium"
              className={cn(className, { deposits: depositsAvailable })}
              data-testid="offer-location"
              startIcon={<LineMapMarkerIcon />}
            >
              {location} • <TextLink href="#getting-there">view map</TextLink>
            </BodyTextBlock>
            <OfferPagePropertyTitle propertyName={property.name} />
            <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>

            {showUserReviewsRating(property.rating, property.reviewsTotal) && (
              <NumberRating
                link
                inlineLabel
                variant="small"
                total={property.reviewsTotal}
                rating={property.rating}
                ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : undefined}
              />
            )}
          </VerticalSpacer>
          {property.logoImageId &&
            <PropertyLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={property.logoImageId}
              alt={`${property.name || 'Vendor'} logo`}
            />}
        </Header>
        {showBadge && <OfferBadgeTag badge={offer.badge} />}

        <OfferIntroSection variant="large">
          {offer.description && <OfferDescription description={offer.description} />}
          {offer.additionalDescription && <MarkdownRender type="compact" content={offer.additionalDescription} />}
          {isMapEnabled && property && <Breakpoint max="mobile">
            <PropertyMap
              height={OFFER_SUMMARY_MAP_HEIGHT}
              wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
              markers={[{
                id: property.id,
                type: PROPERTY_MAP_MARKER_TYPE,
                latitude: property.latitude,
                longitude: property.longitude,
              }]} />
          </Breakpoint>}
          {offer.vimeoVideoId && <WatchVideoButton kind="secondary" startIcon={ <SolidPlayIcon />} onClick={onWatchVideo}>
            Play video
          </WatchVideoButton>}
          {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
        </OfferIntroSection>
        <SummaryBlock>
          <div>
            { urgencyToday.isActive && <OfferViews>
              <InlineGroup vertical="center">
                <urgencyToday.Icon />&nbsp;
                <Caption variant="medium" weight="bold">{urgencyToday.captionText}</Caption>
              </InlineGroup>
            </OfferViews> }
            <Summary onViewPackageOptions={onViewPackageOptions} />
            {isMapEnabled && property && <PropertyMap
              height={OFFER_SUMMARY_MAP_HEIGHT}
              wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
              markers={[{ id: property.id, type: PROPERTY_MAP_MARKER_TYPE, latitude: property.latitude, longitude: property.longitude }]}
            />}
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
        <MobileBNPLAd prices={allPrices} />
        {(shouldShowSalesPhoneUSP || promotions.length > 0) && <ContactUSP />}
      </OfferIntroGrid>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  promotions: getTacticalPackageOptions(state, appState),
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice, 300),
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  checkIn: state.checkInDate,
  customerPortalURL: appState.config.customerPortalURL,
  memberId: appState.auth.account.memberId,
  offerExtra: getOfferExtra(appState, state.offer?.id),
  pathname: appState.router.location.pathname,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isMapEnabled: isIntroMapEnabled(appState),
}))(AlwaysOnIntro)
